/**
 * AllocationsTab — primary operator surface for browsing per-tenant allocations.
 *
 * WQ-230 (Phase 4.9 α4). Renders a filterable table of allocations with quota
 * progress columns, status filter, capability_type filter, and row→drawer.
 *
 * If `tenantId` prop is provided, the tab is scoped to one tenant (used inside
 * TenantDetailsModal). If undefined, shows the cross-tenant view (used by
 * the standalone AllocationsPage).
 *
 * Polls every 30s. Drawer/modal actions trigger immediate refresh on close.
 *
 * WQ-262 polish (Phase 4.9 closer):
 * - Tenant column shows friendly name from a one-shot tenant fetch on mount
 *   (UUID prefix fallback if the lookup fails or tenant is orphaned).
 * - System tenants (`is_system=true`, e.g. Default Organization) hidden by
 *   default to match the platform-wide operator-UI convention; toggle reveals.
 * - Tenant-name search input (case-insensitive substring) for scale.
 * - QUOTA / PERIOD column header tooltip explains free-tier semantics so the
 *   em-dash + "(no quota)" copy doesn't read as broken to non-engineers.
 *
 * The tenant fetch is deliberately decoupled from the 30s allocation poll —
 * tenant data is stable hours-to-days, so we fetch once on mount and reuse.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon } from '../common/Icons';
import {
  listAllocations as defaultListAllocations,
  getAllocationUsage as defaultGetAllocationUsage,
  type Allocation,
  type AllocationFilters,
} from '../../services/allocationAdminService';
import { fetchAllTenants } from '../../features/tenants/api/tenantApi';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../ui';
import AllocationGrantModal from './AllocationGrantModal';
import AllocationDetailDrawer from './AllocationDetailDrawer';

/** Subset of the allocation service surface used by the read-only paths.
 * Tenants supply this from `consumptionService` (WQ-231 α5); operators
 * default to the admin client. */
export interface AllocationsReadService {
  listAllocations: typeof defaultListAllocations;
  getAllocationUsage: typeof defaultGetAllocationUsage;
}

const POLL_INTERVAL_MS = 30_000;

const STATUS_OPTIONS = ['active', 'revoked', 'expired', 'pending'];
const CAPABILITY_TYPE_OPTIONS = [
  'prediction',
  'automl',
  'monte_carlo',
  'fairness',
  'explainability',
  'data_quality',
  'dataset_composition',
  'model',
  'dataset',
  'ai_action',
];

/** WQ-262: minimal tenant info for the lookup map. */
interface TenantInfo {
  id: string;
  name: string;
  is_system: boolean;
}

interface AllocationsTabProps {
  /** When set, the tab is scoped to a single tenant (used inside TenantDetailsModal).
   * When undefined, cross-tenant view (used by /admin/allocations page). */
  tenantId?: string;
  /** Operator email used as default for granted_by/revoked_by inputs. */
  currentUserEmail?: string;
  /** WQ-231 α5: when true, hides Grant / Apply Tier Preset / row actions and
   * passes readOnly through to the drawer. Used by MyCapabilitiesPage to render
   * the same table for tenant self-service. Operator paths leave this unset. */
  readOnly?: boolean;
  /** WQ-231 α5: alternate service implementation. Tenants pass
   * `consumptionService` (which calls /api/tenant/me/capabilities/*); operators
   * leave this unset to use the admin client. */
  serviceOverride?: AllocationsReadService;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'active'
      ? 'badge badge-success'
      : status === 'revoked'
      ? 'badge badge-warning'
      : status === 'expired'
      ? 'badge badge-secondary'
      : 'badge badge-info';
  return <span className={cls}>{status}</span>;
};

const QuotaCell: React.FC<{ used: number; quota: number | null }> = ({ used, quota }) => {
  if (quota == null || quota <= 0) {
    return (
      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
        {used.toLocaleString()} (no quota)
      </span>
    );
  }
  const pct = Math.min(100, Math.round((used / quota) * 100));
  const color =
    pct >= 100
      ? 'var(--danger, #dc2626)'
      : pct >= 80
      ? 'var(--warning, #eab308)'
      : 'var(--success, #22c55e)';
  return (
    <div style={{ minWidth: '120px' }}>
      <div style={{ fontSize: '12px', marginBottom: '2px' }}>
        {used.toLocaleString()} / {quota.toLocaleString()} ({pct}%)
      </div>
      <div
        style={{
          height: '6px',
          background: 'rgba(0,0,0,0.1)',
          borderRadius: '3px',
          overflow: 'hidden',
        }}
      >
        <div
          data-testid="quota-progress-fill"
          style={{ height: '100%', width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

const AllocationsTab: React.FC<AllocationsTabProps> = ({
  tenantId,
  currentUserEmail = '',
  readOnly = false,
  serviceOverride,
}) => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const listAllocations = serviceOverride?.listAllocations ?? defaultListAllocations;
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [capabilityTypeFilter, setCapabilityTypeFilter] = useState<string>('');
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantModalMode, setGrantModalMode] = useState<'grant' | 'tier-preset'>('grant');
  const [selectedAllocation, setSelectedAllocation] = useState<Allocation | null>(null);

  // WQ-262: tenant lookup state. Default OFF for system tenants matches the
  // platform-wide convention used by the dashboard's Active Tenants tile and
  // the Tenant Management page.
  const [tenantMap, setTenantMap] = useState<Record<string, TenantInfo>>({});
  const [showSystemTenants, setShowSystemTenants] = useState(false);
  const [tenantNameFilter, setTenantNameFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: AllocationFilters = {};
      if (tenantId) filters.tenant_id = tenantId;
      if (statusFilter) filters.status = statusFilter;
      if (capabilityTypeFilter) filters.capability_type = capabilityTypeFilter;
      const data = await listAllocations(authFetch, filters);
      setAllocations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load allocations');
    } finally {
      setLoading(false);
    }
  }, [authFetch, tenantId, statusFilter, capabilityTypeFilter]);

  useEffect(() => {
    if (authLoading) return;
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [authLoading, fetchData]);

  // WQ-262: one-shot tenant fetch on mount. Decoupled from the 30s allocation
  // poll because tenant data is stable hours-to-days; refetching every 30s
  // would burn backend cycles for no operator benefit. Skip entirely in
  // single-tenant scope (the lookup is N/A there) and in readOnly mode (the
  // tenant-portal context renders only its own allocations, so friendly-name
  // resolution doesn't add value). Failure is non-fatal: the UUID-prefix
  // fallback in the row render keeps the page functional.
  useEffect(() => {
    if (authLoading) return;
    if (tenantId || readOnly) return;
    // WQ-347 (DEF-092): pages at the API's cap; one 1,000-tenant request was a 422.
    fetchAllTenants(authFetch)
      .then((tenants) => {
        const map: Record<string, TenantInfo> = {};
        for (const t of tenants) {
          map[t.id] = {
            id: t.id,
            name: t.name,
            is_system: t.is_system ?? false,
          };
        }
        setTenantMap(map);
      })
      .catch((err) => {
        // Non-fatal: fall back to UUID prefix display silently.
        console.warn('Tenant lookup failed; showing UUID prefixes:', err);
      });
  }, [authLoading, tenantId, readOnly, authFetch]);

  // WQ-262: derive visible allocations by composing the system-tenant toggle
  // and the tenant-name search filter. Both filters short-circuit cleanly so
  // unknown tenants (orphaned or fetch-failed) don't get hidden when filters
  // aren't actively narrowing — operators can still see them and investigate.
  const visibleAllocations = useMemo(() => {
    const q = tenantNameFilter.trim().toLowerCase();
    return allocations.filter((a) => {
      const tenant = tenantMap[a.tenant_id];
      if (!tenant) {
        // Orphaned: only include when no name filter active. (If the operator
        // is searching by name, an unknown row can't match by name anyway.)
        return q === '';
      }
      if (tenant.is_system && !showSystemTenants) return false;
      if (q !== '' && !tenant.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allocations, tenantMap, showSystemTenants, tenantNameFilter]);

  const handleGrantClose = (didCommit: boolean) => {
    setGrantModalOpen(false);
    if (didCommit) fetchData();
  };

  const handleDrawerClose = (didCommit: boolean) => {
    setSelectedAllocation(null);
    if (didCommit) fetchData();
  };

  const openGrant = () => {
    setGrantModalMode('grant');
    setGrantModalOpen(true);
  };

  const openTierPreset = () => {
    setGrantModalMode('tier-preset');
    setGrantModalOpen(true);
  };

  return (
    <div data-testid="allocations-tab">
      {/* Header: filters + actions */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
          Status:
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Status Filter"
          >
            <option value="">all</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
          Capability:
          <select
            value={capabilityTypeFilter}
            onChange={(e) => setCapabilityTypeFilter(e.target.value)}
            aria-label="Capability Type Filter"
          >
            <option value="">all</option>
            {CAPABILITY_TYPE_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        {/* WQ-262: tenant-name search + system-tenant toggle. Only shown in
            cross-tenant operator mode; meaningless in scoped or readOnly. */}
        {!tenantId && !readOnly && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
              Tenant:
              <input
                type="text"
                value={tenantNameFilter}
                onChange={(e) => setTenantNameFilter(e.target.value)}
                placeholder="search by name"
                aria-label="Tenant Name Filter"
                style={{ minWidth: '160px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={showSystemTenants}
                onChange={(e) => setShowSystemTenants(e.target.checked)}
                aria-label="Show System Tenants"
              />
              Show system tenants
            </label>
          </>
        )}
        <button
          className="btn btn-ghost btn-sm"
          onClick={fetchData}
          disabled={loading}
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshIcon size={14} className={loading ? 'spin' : ''} />
        </button>
        {!readOnly && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary btn-sm" onClick={openTierPreset}>
              Apply Tier Preset
            </button>
            <button className="btn btn-primary btn-sm" onClick={openGrant}>
              Grant
            </button>
          </div>
        )}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '8px 12px',
            background: 'rgba(220,38,38,0.1)',
            border: '1px solid rgba(220,38,38,0.4)',
            borderRadius: '6px',
            color: 'var(--danger, #dc2626)',
            fontSize: '13px',
            marginBottom: '12px',
          }}
        >
          {error}
        </div>
      )}

      {/* Empty state — uses visibleAllocations so filter-empty and data-empty
          both surface the operator path. WQ-262 hint distinguishes the two. */}
      {!loading && visibleAllocations.length === 0 && !error && (
        <div
          data-testid="allocations-empty-state"
          style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}
        >
          <p style={{ marginBottom: '12px' }}>
            {readOnly
              ? 'No capabilities have been allocated to your organization yet. Contact your administrator if you expect to see entries here.'
              : allocations.length > 0
              ? 'No allocations match the active filters.'
              : 'No allocations found.'}
          </p>
          {!readOnly && allocations.length === 0 && (
            <button className="btn btn-primary btn-sm" onClick={openGrant}>
              Grant first allocation
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {visibleAllocations.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: '13px' }}>
            <thead>
              <tr>
                {!tenantId && <th style={{ textAlign: 'left' }}>Tenant</th>}
                <th style={{ textAlign: 'left' }}>Capability Type</th>
                <th style={{ textAlign: 'left' }}>Capability ID</th>
                <th style={{ textAlign: 'left' }}>Tier</th>
                <th style={{ textAlign: 'left' }}>
                  Quota / Period{' '}
                  {/* WQ-262: tooltip clarifies free-tier semantics so the
                      em-dash and "(no quota)" copy don't read as broken. */}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          aria-label="Quota column help"
                          data-testid="quota-tooltip-trigger"
                          style={{
                            display: 'inline-block',
                            cursor: 'help',
                            color: 'var(--text-secondary)',
                            fontWeight: 'normal',
                            marginLeft: '2px',
                          }}
                        >
                          ?
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Free-tier capabilities have no quota enforcement applied. — = no
                        quota set; "(no quota)" in the Usage column means the same.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
                <th style={{ textAlign: 'left' }}>Usage</th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th style={{ textAlign: 'left' }}>Granted</th>
              </tr>
            </thead>
            <tbody>
              {visibleAllocations.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => setSelectedAllocation(a)}
                  style={{ cursor: 'pointer' }}
                  data-testid={`allocation-row-${a.id}`}
                >
                  {/* WQ-262: friendly tenant name with UUID-prefix fallback.
                      Fallback covers: (a) tenant fetch failed, (b) orphan row
                      whose tenant was deleted but allocation row preserved. */}
                  {!tenantId && (
                    <td>
                      {tenantMap[a.tenant_id]?.name ?? `${a.tenant_id.slice(0, 8)}…`}
                    </td>
                  )}
                  <td>{a.capability_type}</td>
                  <td>{a.capability_id ? `${a.capability_id.slice(0, 8)}…` : '*'}</td>
                  <td>{a.tier_preset || '—'}</td>
                  <td>
                    {a.quota_units != null
                      ? `${a.quota_units.toLocaleString()} / ${a.quota_period}`
                      : '—'}
                  </td>
                  <td>
                    <QuotaCell used={a.current_used ?? 0} quota={a.quota_units} />
                  </td>
                  <td>
                    <StatusBadge status={a.status} />
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {a.granted_at ? new Date(a.granted_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!readOnly && (
        <AllocationGrantModal
          isOpen={grantModalOpen}
          mode={grantModalMode}
          scopedTenantId={tenantId}
          currentUserEmail={currentUserEmail}
          onClose={handleGrantClose}
        />
      )}
      <AllocationDetailDrawer
        allocation={selectedAllocation}
        currentUserEmail={currentUserEmail}
        readOnly={readOnly}
        serviceOverride={serviceOverride}
        onClose={handleDrawerClose}
      />
    </div>
  );
};

export default AllocationsTab;
